// ==========================================
// Poolaroo Article Widgets
// Container file for customer-facing JS widgets embedded in blog articles
// (e.g. calculators, interactive instructional tools).
//
// Conventions:
//  - Each widget is gated on a unique container `id` placed in the article
//    body via Shopify admin's article editor (HTML mode).
//  - Widget code silently no-ops when its container is absent — the script
//    loads on every article page but only the matching widget(s) activate.
//  - One `// WIDGET N: <name>` block per widget. Append, don't interleave.
// ==========================================

// GA4 analytics helper the widgets call. Same function as the one in
// custom-product-widgets.js, which loads on product pages only, so article
// pages never had it (until 2026-10-16 the salt calculator's events were all
// silently skipped). Defined here only if a product-page copy isn't present.
if (typeof window.sendAnalyticsEvent !== 'function') {
  window.sendAnalyticsEvent = function(toolName, filterType, inputValue) {
    if (typeof gtag === 'function') {
      gtag('event', 'use_calculator', {
        'tool_name': toolName,
        'filter_type': filterType,
        'input_value': inputValue
      });
    } else if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'use_calculator',
        'tool_name': toolName,
        'filter_type': filterType,
        'input_value': inputValue
      });
    }
  };
}

// ==========================================
// WIDGET 1: Pool Salt Balancing Calculator
// Date: 2026-05-20
// Container: #poolaroo-salt-calculator-target
// ==========================================
document.addEventListener("DOMContentLoaded", function() {
  var container = document.getElementById("poolaroo-salt-calculator-target");
  if (!container) return;

  var widgetHTML = `
    <style>
      #salt-calc-widget {
        background-color: #f4f6f8; border: 1px solid #dcdcdc; border-radius: 8px;
        padding: 20px; margin: 20px 0; font-family: inherit; max-width: 600px;
        box-sizing: border-box;
      }
      #salt-calc-widget *, #salt-calc-widget *::before, #salt-calc-widget *::after { box-sizing: border-box; }
      #salt-calc-widget .salt-header { display: flex; align-items: center; gap: 10px; margin-bottom: 5px; }
      #salt-calc-widget .salt-header svg { flex-shrink: 0; }
      #salt-calc-widget h3 { margin: 0; color: #003366; font-size: 1.25em; }
      #salt-calc-widget .salt-subtext { font-size: 0.9em; color: #555; margin: 0 0 18px 0; }
      #salt-calc-widget .salt-form-group { margin-bottom: 15px; }
      #salt-calc-widget .salt-form-group label { display: block; font-weight: bold; margin-bottom: 5px; color: #333; }
      #salt-calc-widget .salt-form-group select,
      #salt-calc-widget .salt-form-group input {
        width: 100%; padding: 10px; border: 1px solid #ccc; border-radius: 4px;
        box-sizing: border-box; font-size: 1em; background-color: #fff;
      }
      #salt-calc-widget .salt-form-group input:disabled { background-color: #e9ecef; color: #888; }
      #salt-calc-widget .salt-helper { font-size: 0.82em; color: #666; margin-top: 4px; font-style: italic; }
      #salt-calc-widget .salt-calc-btn {
        width: 100%; background-color: #0088cc; color: #fff; border: none;
        padding: 12px 15px; border-radius: 4px; font-size: 1em; font-weight: bold;
        cursor: pointer; transition: background-color 0.2s; margin-top: 5px;
      }
      #salt-calc-widget .salt-calc-btn:hover { background-color: #005f8f; }
      #salt-results-box {
        background-color: #fff; border-left: 6px solid #0088cc; padding: 16px;
        margin-top: 18px; display: none; box-shadow: 0 2px 5px rgba(0,0,0,0.05);
        border-radius: 4px;
      }
      #salt-results-box .result-highlight {
        font-weight: bold; color: #003366; font-size: 1.35em; margin: 0 0 4px 0;
      }
      #salt-results-box .result-sub {
        font-size: 0.9em; color: #555; margin-bottom: 12px;
        padding-bottom: 10px; border-bottom: 1px solid #eee;
      }
      #salt-results-box .salt-section { margin-top: 12px; }
      #salt-results-box .salt-section strong.salt-section-title {
        display: block; font-size: 0.82em; text-transform: uppercase; letter-spacing: 0.5px;
        color: #0088cc; margin-bottom: 6px;
      }
      #salt-results-box .salt-section p, #salt-results-box .salt-section ul {
        margin: 4px 0; font-size: 0.92em; line-height: 1.5; color: #333;
      }
      #salt-results-box .salt-section ul { padding-left: 20px; }
      #salt-results-box .salt-good {
        background-color: #e6f5ea; border: 1px solid #8cc99b; color: #1f6b35;
        padding: 10px 12px; border-radius: 4px; font-size: 0.92em; margin-top: 10px;
      }
      #salt-results-box .salt-warn {
        background-color: #fffbe6; border: 1px solid #efe8b1; color: #7a4e00;
        padding: 10px 12px; border-radius: 4px; font-size: 0.88em; margin-top: 12px;
      }
      #salt-results-box .salt-error {
        background-color: #fdecea; border: 1px solid #f5b7b1; color: #8b1e16;
        padding: 10px 12px; border-radius: 4px; font-size: 0.92em;
      }
    </style>

    <div id="salt-calc-widget">
      <div class="salt-header">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0088cc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 2s-6 8-6 13a6 6 0 0 0 12 0c0-5-6-13-6-13z"/>
          <circle cx="10" cy="15" r="0.9" fill="#0088cc"/>
          <circle cx="13.2" cy="13" r="0.9" fill="#0088cc"/>
          <circle cx="13.5" cy="16.7" r="0.9" fill="#0088cc"/>
        </svg>
        <h3>Pool Salt Balancing Calculator</h3>
      </div>
      <p class="salt-subtext">Figure out exactly how much pool salt to add to reach your target level.</p>

      <div class="salt-form-group">
        <label for="salt-current-state">Current Salt Level:</label>
        <select id="salt-current-state">
          <option value="">-- Select Current State --</option>
          <option value="fresh">Freshly Filled / New Pool Startup (0 ppm)</option>
          <option value="custom">Enter Test Reading (ppm)</option>
        </select>
      </div>

      <div class="salt-form-group">
        <label for="salt-current-ppm">Current PPM Reading:</label>
        <input type="number" id="salt-current-ppm" placeholder="e.g. 2400" min="0" max="6000" disabled />
        <div class="salt-helper">Use a test strip, drop kit, or digital salt meter for an accurate reading.</div>
      </div>

      <div class="salt-form-group">
        <label for="salt-gallons">Pool Size (Gallons):</label>
        <input type="number" id="salt-gallons" placeholder="e.g. 20000" min="500" max="200000" />
      </div>

      <div class="salt-form-group">
        <label for="salt-target">Target Salt Level:</label>
        <select id="salt-target">
          <option value="3200" selected>3,200 ppm (typical — most SWGs)</option>
          <option value="3000">3,000 ppm (lower end)</option>
          <option value="3400">3,400 ppm (Pentair IntelliChlor)</option>
          <option value="3500">3,500 ppm (Hayward AquaRite)</option>
        </select>
      </div>

      <button class="salt-calc-btn" id="salt-calc-btn">Calculate Salt Needed</button>

      <div id="salt-results-box"></div>
    </div>
  `;

  container.innerHTML = widgetHTML;

  var stateSelect = document.getElementById('salt-current-state');
  var ppmInput    = document.getElementById('salt-current-ppm');
  var gallonsInput = document.getElementById('salt-gallons');
  var targetSelect = document.getElementById('salt-target');
  var calcBtn      = document.getElementById('salt-calc-btn');
  var resultsBox   = document.getElementById('salt-results-box');

  var ppmTimer, gallonsTimer;

  stateSelect.addEventListener('change', function() {
    if (stateSelect.value === 'fresh') {
      ppmInput.value = 0;
      ppmInput.disabled = true;
    } else if (stateSelect.value === 'custom') {
      ppmInput.disabled = false;
      ppmInput.value = '';
      ppmInput.focus();
    } else {
      ppmInput.disabled = true;
      ppmInput.value = '';
    }
    if (typeof window.sendAnalyticsEvent === 'function') window.sendAnalyticsEvent('Salt_Balancing', 'Current_State', stateSelect.value);
  });

  ppmInput.addEventListener('input', function() {
    clearTimeout(ppmTimer);
    ppmTimer = setTimeout(function() {
      if (typeof window.sendAnalyticsEvent === 'function') window.sendAnalyticsEvent('Salt_Balancing', 'PPM_Input', ppmInput.value);
    }, 2000);
  });

  gallonsInput.addEventListener('input', function() {
    clearTimeout(gallonsTimer);
    gallonsTimer = setTimeout(function() {
      if (typeof window.sendAnalyticsEvent === 'function') window.sendAnalyticsEvent('Salt_Balancing', 'Gallons_Input', gallonsInput.value);
    }, 2000);
  });

  targetSelect.addEventListener('change', function() {
    if (typeof window.sendAnalyticsEvent === 'function') window.sendAnalyticsEvent('Salt_Balancing', 'Target_Select', targetSelect.value);
  });

  calcBtn.addEventListener('click', function() {
    var currentPPM = parseFloat(ppmInput.value);
    var gallons    = parseFloat(gallonsInput.value);
    var targetPPM  = parseFloat(targetSelect.value);

    if (stateSelect.value === '') {
      renderError('Please select your current salt level.');
      return;
    }
    if (isNaN(currentPPM) || currentPPM < 0) {
      renderError('Please enter a valid current PPM reading.');
      return;
    }
    if (isNaN(gallons) || gallons < 500) {
      renderError('Please enter a valid pool size (minimum 500 gallons).');
      return;
    }

    if (currentPPM >= targetPPM) {
      renderAtOrAbove(currentPPM, targetPPM);
      if (typeof window.sendAnalyticsEvent === 'function') window.sendAnalyticsEvent('Salt_Balancing', 'Result', 'at_or_above_target');
      return;
    }

    // Standard pool industry formula:
    // lbs salt = (target_ppm - current_ppm) * gallons * 8.34 / 1,000,000
    var ppmDelta = targetPPM - currentPPM;
    var lbsNeeded = (ppmDelta * gallons * 8.34) / 1000000;
    var lbsRounded = Math.round(lbsNeeded * 10) / 10;

    // 40 lb is the standard pool salt bag size
    var bags40 = lbsNeeded / 40;
    var bagsWhole = Math.floor(bags40);
    var bagsRemainderLbs = Math.round((bags40 - bagsWhole) * 40);

    // Granular pool salt: ~1 lb per ~1.6 cups (approx — coarse pool salt density)
    var cups = Math.round(lbsRounded * 1.6 * 10) / 10;

    renderResult({
      lbs: lbsRounded,
      bagsWhole: bagsWhole,
      bagsRemainderLbs: bagsRemainderLbs,
      cups: cups,
      currentPPM: currentPPM,
      targetPPM: targetPPM,
      ppmDelta: ppmDelta,
      gallons: gallons
    });

    if (typeof window.sendAnalyticsEvent === 'function') window.sendAnalyticsEvent('Salt_Balancing', 'Calculated', lbsRounded + 'lbs/' + gallons + 'gal');
  });

  function renderError(msg) {
    resultsBox.style.display = 'block';
    resultsBox.innerHTML = '<div class="salt-error">⚠ ' + msg + '</div>';
  }

  function renderAtOrAbove(current, target) {
    resultsBox.style.display = 'block';
    resultsBox.innerHTML =
      '<div class="result-highlight">You\'re Already at Target!</div>' +
      '<div class="salt-good">Your reading of <strong>' + current.toLocaleString() + ' ppm</strong> is at or above the target of <strong>' + target.toLocaleString() + ' ppm</strong>. No salt needs to be added.<br><br>' +
      'If your salt cell is reporting LOW, re-test with fresh strips or a digital salt meter — cell readings can drift, especially in cold water or with scale buildup.</div>';
  }

  function renderResult(r) {
    var bagText;
    if (r.bagsWhole === 0) {
      bagText = 'Less than one 40 lb bag (~' + r.lbs + ' lbs)';
    } else if (r.bagsRemainderLbs < 3) {
      bagText = r.bagsWhole + ' × 40 lb bag' + (r.bagsWhole > 1 ? 's' : '');
    } else {
      bagText = r.bagsWhole + ' × 40 lb bag' + (r.bagsWhole > 1 ? 's' : '') + ' + ~' + r.bagsRemainderLbs + ' lbs';
    }

    var measureNote;
    if (r.lbs < 5) {
      measureNote = '<p>For small top-offs like this, a standard kitchen measuring cup works fine — roughly <strong>' + r.cups + ' cups</strong> of granular pool salt.</p>';
    } else {
      measureNote = '<p>Easiest method: use full 40 lb bags wherever possible. For odd remainders, a clean 5-gallon bucket on a bathroom scale gets ye accurate weights without any fancy gear. (Roughly 1 lb of pool salt fills about a 16 oz plastic cup.)</p>';
    }

    var bigDoseWarn = (r.ppmDelta > 1500) ?
      '<div class="salt-warn">⚠ This is a sizeable dose. Consider splitting it into two applications 24 hours apart, re-testing in between to dial in your target.</div>'
      : '';

    resultsBox.style.display = 'block';
    resultsBox.innerHTML =
      '<div class="result-highlight">Add ~' + r.lbs.toLocaleString() + ' lbs of Pool Salt</div>' +
      '<div class="result-sub">' + bagText + ' &nbsp;•&nbsp; raises ' + r.ppmDelta.toLocaleString() + ' ppm in ' + r.gallons.toLocaleString() + ' gallons</div>' +

      '<div class="salt-section">' +
        '<strong class="salt-section-title">How to Apply</strong>' +
        '<ul>' +
          '<li>Use <em>pool-grade salt only</em> — 99%+ pure NaCl, non-iodized, no anti-caking agents. Avoid water softener or table salt.</li>' +
          '<li>Run the pump on <strong>circulate</strong> (not filter) for ~24 hours to fully dissolve.</li>' +
          '<li>Broadcast salt slowly across the deep end. Brush any salt that settles on the floor to help it dissolve faster.</li>' +
          '<li>Keep your salt cell <strong>OFF</strong> until fully dissolved (about 24 hrs) to protect the cell.</li>' +
        '</ul>' +
      '</div>' +

      '<div class="salt-section">' +
        '<strong class="salt-section-title">Easy Ways to Measure</strong>' +
        measureNote +
      '</div>' +

      '<div class="salt-section">' +
        '<strong class="salt-section-title">Double-Check Your Reading</strong>' +
        '<p>Before dosing, confirm yer current ppm with a fresh test strip — or better yet, a digital salt meter. Salt cell display readings can drift ±200–400 ppm and shouldn\'t be yer only source.</p>' +
        '<p>After adding salt and circulating 24 hours, re-test before adding more. It\'s far easier to add salt than remove it (which requires partial draining).</p>' +
      '</div>' +

      bigDoseWarn;
  }
});
